import React, { useEffect } from 'react';
import { ExerciseDefinition, MuscleGroup } from '../../types';
import { useTranslation } from '../../i18n/provider';
import styles from './ExerciseSelectionModal.module.css';

interface ExerciseSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectExercise: (exercise: ExerciseDefinition) => void;
  exercises: ExerciseDefinition[];
  selectedMuscleGroup: MuscleGroup;
  onMuscleGroupChange: (group: MuscleGroup) => void;
  muscleGroups: MuscleGroup[];
}

const ExerciseSelectionModal: React.FC<ExerciseSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectExercise,
  exercises,
  selectedMuscleGroup,
  onMuscleGroupChange,
  muscleGroups
}) => {
  const { t } = useTranslation();
  const formatMuscleGroupLabel = (group: string) => {
    const fallback = group.charAt(0).toUpperCase() + group.slice(1);
    return t(`muscleGroups.${group}`, { defaultValue: fallback });
  };
  // Prevent background (page) scroll while modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const filteredExercises = selectedMuscleGroup === 'all' 
    ? exercises 
    : exercises.filter(ex => ex.muscleGroup === selectedMuscleGroup);

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{t('exercises.selection.title')}</h3>
          <button className={styles.closeButton} onClick={onClose} aria-label={t('exercises.modal.close')}>×</button>
        </div>
        
        <div className={styles.modalBody}>
          <div className={styles.filterOptions}>
            {muscleGroups.map(group => (
              <button
                key={group}
                className={`${styles.filterOption} ${selectedMuscleGroup === group ? styles.active : ''}`}
                onClick={() => onMuscleGroupChange(group)}
              >
                {formatMuscleGroupLabel(group)}
              </button>
            ))}
          </div>
          
          <div className={styles.exercisesList}>
            {filteredExercises.map(exercise => (
              <div 
                key={exercise.id} 
                className={styles.exerciseItem}
                onClick={() => onSelectExercise(exercise)}
              >
                <div className={styles.exerciseImage} style={{ backgroundImage: `url(${exercise.imageUrl})` }} />
                <div className={styles.exerciseInfo}>
                  <div className={styles.exerciseName}>{exercise.localizedName || exercise.name}</div>
                  <div className={styles.exerciseMuscle}>
                    {formatMuscleGroupLabel(exercise.muscleGroup)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button className="button button--secondary" onClick={onClose}>{t('exercises.modal.cancel')}</button>
        </div>
      </div>
    </div>
  );
};

export default ExerciseSelectionModal;
